import { HydrationAddress, HydrationName, HydrationPrimaryOwner } from '../hydration.models';
import { firstAddressLine } from '../hydration.utils';
import type { CooAddress, CooEmploymentDetails, CooPrimaryOwner } from '../ku4-draft.models';

const STATUS_REVERSE: Record<string, string> = {
  EMPLOYED: 'E',
  SELF_EMPLOYED: 'SE',
  VANGUARD_EMPLOYED: 'VE',
  RETIRED: 'R',
  UNEMPLOYED: 'U',
};

export function hydratePrimaryOwner(owner?: CooPrimaryOwner): HydrationPrimaryOwner | undefined {
  if (!owner) {
    return undefined;
  }

  const result: HydrationPrimaryOwner = {};

  const name = buildName(owner);
  if (name) {
    result.name = name;
  }

  if (owner.birthDate) {
    result.dateOfBirth = owner.birthDate;
  }

  if (owner.citizenshipStatus) {
    result.citizenshipStatus = owner.citizenshipStatus;
  }

  if (owner.email) {
    result.email = owner.email;
  }

  if (owner.ssn) {
    result.ssn = owner.ssn;
  }

  if (owner.gender) {
    result.gender = owner.gender;
  }

  if (owner.countryOfCitizenship) {
    result.countryOfCitizenship = owner.countryOfCitizenship;
  }

  if (owner.suffix?.code) {
    result.suffix = owner.suffix.code;
  }

  const employment = buildEmployment(owner.employment);
  if (employment) {
    result.employment = employment;
  }

  if (owner.annualIncomeCode) {
    result.annualIncome = owner.annualIncomeCode;
  }

  if (owner.liquidNetWorthCode) {
    result.liquidNetWorth = owner.liquidNetWorthCode;
  }

  const primaryResidence = buildAddress(owner.primaryResidence);
  if (primaryResidence) {
    result.primaryResidence = primaryResidence;
  }

  const primaryMailing = buildAddress(owner.primaryMailing);
  if (primaryMailing) {
    result.primaryMailing = primaryMailing;
  }

  if (owner.primaryPhone?.phoneNumber) {
    result.phoneNumber = owner.primaryPhone.phoneNumber;
  }

  if (owner.primaryPhone?.mobile !== undefined) {
    result.isMobile = owner.primaryPhone.mobile;
  }

  return Object.keys(result).length ? result : undefined;
}

function buildName(owner: CooPrimaryOwner): HydrationName | undefined {
  const name: HydrationName = {};

  if (owner.firstName) {
    name.firstName = owner.firstName;
  }
  if (owner.middleName) {
    name.middleName = owner.middleName;
  }
  if (owner.lastName) {
    name.lastName = owner.lastName;
  }

  return Object.keys(name).length ? name : undefined;
}

function buildEmployment(
  employment?: CooEmploymentDetails,
): HydrationPrimaryOwner['employment'] | undefined {
  if (!employment) {
    return undefined;
  }

  const result: NonNullable<HydrationPrimaryOwner['employment']> = {};

  if (employment.status && STATUS_REVERSE[employment.status]) {
    result.employmentStatus = STATUS_REVERSE[employment.status];
  }
  if (employment.employerName) {
    result.employer = employment.employerName;
  }
  if (employment.occupation) {
    result.occupation = employment.occupation;
  }

  const employerAddress = buildAddress(employment.employerAddress);
  if (employerAddress) {
    result.employerAddress = employerAddress;
  }

  if (employment.marriedToVanguardEmployee !== undefined) {
    result.marriedToVanguardEmployee = employment.marriedToVanguardEmployee;
  }
  if (employment.finraAffiliated !== undefined) {
    result.finraAffiliated = employment.finraAffiliated;
  }
  if (employment.controlPerson !== undefined) {
    result.controlPerson = employment.controlPerson;
  }
  if (employment.controlCompanies) {
    result.controlCompanies = employment.controlCompanies;
  }

  return Object.keys(result).length ? result : undefined;
}

function buildAddress(address?: CooAddress): HydrationAddress | undefined {
  if (!address) {
    return undefined;
  }

  const result: HydrationAddress = {};

  const street = firstAddressLine(address);
  if (street) {
    result.street = street;
  }
  if (address.city) {
    result.city = address.city;
  }
  if (address.state) {
    result.state = address.state;
  }
  if (address.zip) {
    result.zip = address.zip;
  }
  if (address.zipPlus4) {
    result.zipPlus4 = address.zipPlus4;
  }

  return Object.keys(result).length ? result : undefined;
}
